/**
 * HeirWise Adaptive Learning Engine — brand + rule constants.
 * These constants are UI/copy-safe. Progression laws are also enforced
 * server-side in the Postgres RPCs; the strings here are purely for
 * display and documentation.
 */

import type { LearningEngineDecision } from "./types";

export const HEIRWISE_ENGINE_NAME = "HeirWise Adaptive Learning Engine";
export const HEIRWISE_ENGINE_TAGLINE =
  "The brain behind every personalized learning experience.";
export const HEIRWISE_ENGINE_PROMISE =
  "Fully functional from day one. Smarter over time.";

export const ENGINE_PILLARS = [
  { key: "curriculum_graph", title: "Curriculum Graph", description: "Dynamic pathways built for mastery." },
  { key: "decision_engine", title: "Decision Engine", description: "Intelligent logic that determines the learner's next best action." },
  { key: "mastery_signals", title: "Mastery Signals", description: "Real-time insight into strengths and gaps." },
  { key: "adaptive_support", title: "Adaptive Support", description: "Personalized help when it matters most." },
  { key: "personalized_pathways", title: "Personalized Pathways", description: "Tailored journeys that evolve with every learner." },
  { key: "continuous_optimization", title: "Continuous Optimization", description: "Always learning. Always improving." },
] as const;

export const PROGRESSION_LAWS = [
  "Never auto-complete a lesson because quiz questions failed to load or returned empty.",
  "Never unlock the next lesson unless the current lesson has a passing quiz result.",
  "Never mark a failed module as green.",
  "Never issue rewards twice.",
  "Never allow module retake without reversing eligible module rewards first.",
  "Never allow frontend-only completion state to override backend progress.",
  "When uncertain, return a locked or review state instead of unlocking progression.",
  "Every engine decision includes an audit code and human-readable reason.",
  "Back to Dashboard must never mark a lesson or module complete.",
] as const;

export const SAFE_LOCKED_DECISION: LearningEngineDecision = {
  action: "error_safe_locked",
  reason:
    "The learning engine could not verify progression eligibility. Safely locked pending review.",
  target_module_key: null,
  target_lesson_id: null,
  target_route: "/dashboard",
  is_locked: true,
  lock_reason: "Progression eligibility could not be verified.",
  support_level: "review_recommended",
  reward_impact: "blocked",
  audit_code: "SAFE_LOCK_CLIENT_FALLBACK",
  metadata: {},
};

/** Display label for the primary CTA rendered from an engine decision. */
export function actionCtaLabel(decision: LearningEngineDecision): string {
  switch (decision.action) {
    case "start_first_module": return "Start Your First Step";
    case "start_lesson": return "Start Lesson";
    case "resume_lesson": return "Resume Lesson";
    case "retry_lesson_quiz": return "Retry Quiz";
    case "retake_lesson_required": return "Retake Lesson";
    case "continue_to_next_lesson": return "Continue to Next Lesson";
    case "module_complete": return "View Module Completion";
    case "retake_module_required": return "Review Module";
    case "dashboard_review": return "Review Dashboard";
    case "locked": return "Go to Required Step";
    case "error_safe_locked": return "Review Required";
    default: return "Continue";
  }
}